import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { auth } from "@/api/auth";
import { Button, Field, Input, Notice } from "@/components/ui/Field";
import { AuthCard } from "@/features/auth/AuthLayout";
import { SocialButtons } from "@/features/auth/SocialButtons";
import { applyAuthError, type AuthFailure } from "@/features/auth/formErrors";
import { fieldMessage } from "@/lib/fieldMessage";
import { signUpSchema, type SignUpValues } from "@/features/auth/schemas";
import { sessionChanged } from "@/features/auth/session";

export function SignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<AuthFailure>({});

  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: "", password: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFailure({});
    try {
      await auth.signUp(values.email, values.password);
      // Verification is mandatory, so a 200 here means allauth granted a session anyway.
      await sessionChanged(queryClient);
      await navigate({ to: "/" });
    } catch (error) {
      setFailure(
        applyAuthError(error, {
          setError: form.setError,
          fields: ["email", "password"],
          fallback: t("auth.errors.unexpected"),
        }),
      );
    }
  });

  // The expected outcome: the account exists and allauth is waiting for the emailed link.
  if (failure.pendingFlow === "verify_email") {
    return (
      <AuthCard title={t("auth.verify.sentTitle")} description={t("auth.verify.sentBody")}>
        <Notice tone="info">{t("auth.verify.keepPaper")}</Notice>
        <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
          {t("auth.backToSignIn")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.signUp.title")} description={t("auth.signUp.subtitle")}>
      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          name="email"
          label={t("auth.email")}
          error={fieldMessage(form.formState.errors, "email", t)}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            invalid={!!form.formState.errors.email}
            {...form.register("email")}
          />
        </Field>
        <Field
          name="password"
          label={t("auth.password")}
          error={fieldMessage(form.formState.errors, "password", t)}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!form.formState.errors.password}
            {...form.register("password")}
          />
        </Field>
        {failure.formError && <Notice tone="error">{failure.formError}</Notice>}
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("auth.signUp.working") : t("auth.signUp.submit")}
        </Button>
      </form>

      <SocialButtons />

      <p className="text-center text-sm text-stone-600">
        {t("auth.signUp.haveAccount")}{" "}
        <Link to="/auth/sign-in" className="text-brand-700 underline">
          {t("auth.signIn.submit")}
        </Link>
      </p>
    </AuthCard>
  );
}
